<template>
  <v-card variant="outlined" rounded="lg">
    <v-toolbar density="compact">
      <v-toolbar-title>{{ label }}</v-toolbar-title>
    </v-toolbar>
    <v-table
      fixed-header
      density="compact"
      :style="{ 'max-height': maxHeight ? String(maxHeight) + 'px' : '' }"
    >
      <thead>
        <tr>
          <th class="text-left">Link</th>
          <th v-if="!readonly" class="text-right">Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="link in links" :key="link.id">
          <td>
            <a :href="link.link" target="_blank">
              {{ link.link }}
            </a>
          </td>
          <td v-if="!readonly" class="text-right">
            <v-btn
              icon="mdi-pencil"
              variant="plain"
              density="compact"
              @click="openDialog('Edit', link)"
            />
            <v-btn
              icon="mdi-delete"
              variant="plain"
              color="red"
              density="compact"
              @click="deleteLink(link)"
            />
          </td>
        </tr>
        <tr v-if="!readonly">
          <td></td>
          <td class="text-right">
            <v-btn
              color="primary"
              variant="text"
              class="mt-2 mb-2"
              @click="openDialog('Add')"
            >
              <v-icon>mdi-plus</v-icon>Add
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-table>

    <BaseDialog v-model="dialog" :title="`${dialogMode} Link`">
      <v-text-field
        v-model="link"
        label="Link"
        density="compact"
        variant="outlined"
        hide-details
      />

      <template #actions>
        <v-btn @click="saveLink">{{ dialogMode }}</v-btn>
      </template>
    </BaseDialog>
  </v-card>
</template>

<script setup lang="ts">
import { AssetManagementPlanLink } from "@/classes/asset-management-plan";
import BaseDialog from "@/components/base/dialogs/BaseDialog.vue";
import { useConfirmStore } from "@/store/confirm";
import { useNotifierStore } from "@/store/notifier";
import { ref } from "vue";

type LinkItem = InstanceType<typeof AssetManagementPlanLink>;

const props = defineProps<{
  label: string;
  links: LinkItem[];
  maxHeight?: number;
  readonly?: boolean;
}>();

const emit = defineEmits<{
  add: [link: string];
  update: [payload: { id: string; link: string }];
  delete: [id: string];
}>();

const notifier = useNotifierStore();
const confirm = useConfirmStore();

type DialogMode = "Add" | "Edit";

const dialog = ref(false);
const dialogMode = ref<DialogMode>("Add");
const link = ref("");
const editItem = ref<LinkItem>();

const openDialog = (mode: DialogMode, item?: LinkItem) => {
  dialog.value = true;
  dialogMode.value = mode;
  link.value = item?.link ?? "";
  editItem.value = item;
};

const saveLink = () => {
  if (!URL.canParse(link.value)) {
    notifier.error("Invalid link");
  } else {
    if (dialogMode.value === "Add") {
      emit("add", link.value);
    } else {
      emit("update", { id: editItem.value?.id ?? "", link: link.value });
    }
    dialog.value = false;
  }
};

const deleteLink = (item: LinkItem) => {
  confirm.open({
    title: "Delete Link?",
    confirmText: "Delete",
    color: "red",
    onConfirm: () => {
      emit("delete", item.id);
    },
  });
};
</script>
